import { BLOCKS } from '@contentful/rich-text-types';
import type { DocumentWithEmbeddedEntries } from '@/libs/contentful/types';
import { extractTextFromNode, getIdFromText } from '@/utils/formatters';

/**
 * Matches paragraphs whose entire content is wrapped in `[toc]...[/toc]`
 * (e.g. `"[toc]Travel Perks[/toc]"`), promoting them to sub-section headings.
 * The inner text may be empty (`[toc][/toc]`) for untitled sub-sections —
 * those still get a letter label in the body but are skipped by the TOC.
 * Group 1 is the heading text inside the wrap.
 */
export const TOC_HEADING_RE = /^\s*\[toc\]\s*(.*?)\s*\[\/toc\]\s*$/;

/** Strips any opening or closing `[toc]` marker, along with adjacent whitespace, from a text segment. */
export const TOC_MARKER_RE = /\s*\[\/?toc\]\s*/g;

/**
 * Matches a paragraph whose entire content is a `[below-rev]` marker.
 * Fine print content after the marker renders below the fine print block and
 * the (optional) "Rev." date — migrated agreement pages use this for
 * "Important Information" boxes that WP disclaimer scripts injected there.
 */
export const BELOW_REV_PARAGRAPH_RE = /^\s*\[below-rev\]\s*$/;

/** Strips any `[below-rev]` marker, along with adjacent whitespace, from a text segment. */
export const BELOW_REV_MARKER_RE = /\s*\[below-rev\]\s*/g;

/**
 * Splits a finePrint document at the first `[below-rev]` marker
 * paragraph. Everything before the marker renders in the usual fine print
 * spot; everything after renders below it and the (optional) "Rev." date.
 * The marker paragraph itself is dropped. Without a marker, the whole
 * document is `main`.
 */
export function splitFinePrintAtMarker(finePrint?: DocumentWithEmbeddedEntries): {
  main?: DocumentWithEmbeddedEntries;
  below?: DocumentWithEmbeddedEntries;
} {
  const content = finePrint?.json?.content;
  if (!content) return { main: finePrint };

  const markerIndex = content.findIndex(
    node => node.nodeType === BLOCKS.PARAGRAPH && BELOW_REV_PARAGRAPH_RE.test(extractTextFromNode(node)),
  );
  if (markerIndex === -1) return { main: finePrint };

  const main = content.slice(0, markerIndex);
  const below = content.slice(markerIndex + 1);
  return {
    main: main.length > 0 ? { ...finePrint, json: { ...finePrint.json, content: main } } : undefined,
    below: below.length > 0 ? { ...finePrint, json: { ...finePrint.json, content: below } } : undefined,
  };
}

export function getTocAnchor(sectionAnchor: string, headingText: string, anchorCounts: Map<string, number>): string {
  const baseAnchor = `${sectionAnchor}-${getIdFromText(headingText)}`;
  const count = anchorCounts.get(baseAnchor) ?? 0;
  anchorCounts.set(baseAnchor, count + 1);

  if (count === 0) return baseAnchor;

  return `${baseAnchor}-${count + 1}`;
}
